import React, { useState, useEffect, useContext, useMemo } from 'react';
import axios from 'axios';
import { useExpenseStore } from '../store/useExpenseStore';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Grid,
  Skeleton,
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
} from 'recharts';
import UserDetailsContext from './UserDetailsContext';

const COLORS = [
  '#0088FE', '#00C49F', '#FFBB28', '#FF8042',
  '#AF19FF', '#FF4560', '#00B8D9', '#FF6699',
];

export default function Dashboard() {
  const [expenses, setExpenses] = useState([]);
  const [categoryExpenses, setCategoryExpenses] = useState([]);
  const [dateRange, setDateRange] = useState('week');
  const [category, setCategory] = useState('all');
  const [isLoading, setIsLoading] = useState(true);

  const userDetails = useContext(UserDetailsContext);
  const expenseStore = useExpenseStore();

  const normalizedRole = (userDetails?.userDetails?.UserRole || '').toLowerCase();
  const isOwner = normalizedRole === 'owner' || normalizedRole === 'manager';

  useEffect(() => {
    const details = userDetails?.userDetails;
    if (!details) return;
    const { BusinessID, TeamContactID, UserRole } = details;
    const normalizedRoleKey = String(UserRole || '').toLowerCase() || 'unknown';
    console.log('User Details in Dashboard:', normalizedRoleKey);
    const key = `${normalizedRoleKey}-${BusinessID || ''}-${TeamContactID || ''}`;
    let mounted = true;
    (async () => {
      setIsLoading(true);
      await expenseStore.loadDashboardSummaries({ BusinessID, TeamContactID, UserRole });
      if (!mounted) return;
      const cached = useExpenseStore.getState().getDashboard(key);
      setExpenses(cached.summary);
      setCategoryExpenses(cached.categorySummary);
      setIsLoading(false);
    })();
    return () => { mounted = false; };
  }, [userDetails]);

  const filteredExpenses = expenses;
  console.log('Filtered Expenses 999:', filteredExpenses);

  const stats = {
    total: filteredExpenses.reduce((sum, exp) => sum + (parseFloat(exp.TotalExpenses) || 0), 0),
    pending: filteredExpenses.reduce((sum, exp) => sum + (exp.PendingCount || 0), 0),
    approved: filteredExpenses.reduce((sum, exp) => sum + (exp.ApprovedCount || 0), 0),
    rejected: filteredExpenses.reduce((sum, exp) => sum + (exp.RejectedCount || 0), 0),
  };

  console.log('Stats:', stats);
  const groupSmallCategories = (data) => {
    const sortedData = [...data].sort((a, b) => b.value - a.value);
    const topCategories = sortedData.slice(0, 9);
    const otherCategories = sortedData.slice(9);

    const result = [...topCategories];

    if (otherCategories.length > 0) {
      const othersValue = otherCategories.reduce((sum, item) => sum + item.value, 0);
      result.push({ name: 'Others', value: othersValue });
    }

    return result;
  };

  const categoryData = useMemo(() => {
    const filteredData = categoryExpenses
      .filter(item => category === 'all' || item.Category === category)
      .map(item => ({
        name: item.Category || 'Unknown',
        value: parseFloat(item.TotalExpenses) || 0,
      }));
    
    return groupSmallCategories(filteredData);
  }, [categoryExpenses, category]);

  const categoryColorMap = useMemo(() => {
    const map = {};
    let colorIndex = 0;
    categoryData.forEach(item => {
      if (!map[item.name]) {
        map[item.name] = COLORS[colorIndex % COLORS.length];
        colorIndex++;
      }
    });
    return map;
  }, [categoryData]);

  const selectedCategoryData = categoryExpenses.find(item => item.Category === category);

  const statusData =
    category === 'all'
      ? [
          {
            name: 'Pending',
            value: categoryExpenses.reduce((sum, e) => sum + (e.PendingCount || 0), 0),
          },
          {
            name: 'Approved',
            value: categoryExpenses.reduce((sum, e) => sum + (e.ApprovedCount || 0), 0),
          },
          {
            name: 'Rejected',
            value: categoryExpenses.reduce((sum, e) => sum + (e.RejectedCount || 0), 0),
          },
        ]
      : selectedCategoryData
      ? [
          { name: 'Pending', value: selectedCategoryData.PendingCount || 0 },
          { name: 'Approved', value: selectedCategoryData.ApprovedCount || 0 },
          { name: 'Rejected', value: selectedCategoryData.RejectedCount || 0 },
        ]
      : [
          { name: 'Pending', value: 0 },
          { name: 'Approved', value: 0 },
          { name: 'Rejected', value: 0 },
        ];

  if (isLoading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        <Skeleton variant="text" width={160} height={32} sx={{ mb: 3 }} />
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[0,1,2,3].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Card>
                <CardContent>
                  <Skeleton variant="text" width={120} />
                  <Skeleton variant="text" width={80} height={40} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rectangular" height={330} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rectangular" height={330} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        Dashboard
      </Typography>

      {/* Summary Cards */}
      {filteredExpenses.map((e, index) => (
        <Grid container spacing={2} key={index} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography className='MuiInputLabel-root'>Total Expenses</Typography>
                <Typography variant="h4">{e.TotalExpenses}</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography className='MuiInputLabel-root'>Pending Review</Typography>
                <Typography variant="h4">{e.PendingCount}</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography className='MuiInputLabel-root'>Approved</Typography>
                <Typography variant="h4">{e.ApprovedCount}</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography className='MuiInputLabel-root'>Rejected</Typography>
                <Typography variant="h4">{e.RejectedCount}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ))}

      {/* Filters and Charts (shared for both roles) */}
      <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {/* <FormControl sx={{ minWidth: 150 }} className='custom-select'>
          <InputLabel id="date-range-label" sx={{ color: 'var(--text-primary)' }}>Date Range</InputLabel>
          <Select
            labelId="date-range-label"
            label="Date Range"
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--text-primary)' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--text-primary)' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--text-primary)' },
              '& .MuiSelect-select': { color: 'var(--text-primary)' },
              '& .MuiSvgIcon-root': { color: 'var(--text-primary)' },
            }}
          >
            <MenuItem value="all">All Time</MenuItem>
            <MenuItem value="week">Last Week</MenuItem>
            <MenuItem value="month">Last Month</MenuItem>
          </Select>
        </FormControl> */}

        <FormControl sx={{ minWidth: 160 }} className='custom-select'>
          <InputLabel id="category-label" sx={{ color: 'var(--text-primary)' }}>Category</InputLabel>
          <Select
            labelId="category-label"
            label="Category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--text-primary)' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--text-primary)' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--text-primary)' },
              '& .MuiSelect-select': { color: 'var(--text-primary)' },
              '& .MuiSvgIcon-root': { color: 'var(--text-primary)' },
            }}
          >
            <MenuItem value="all">All Categories</MenuItem>
            {categoryExpenses.map((cat) => (
              <MenuItem key={cat.Category} value={cat.Category}>
                {cat.Category}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Charts */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Expenses by Category
          </Typography>
          <PieChart
            width={window.innerWidth < 768 ? window.innerWidth - 60 : 400}
            height={330}
          >
            <Pie
              data={categoryData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={70}
              label={({ name, percent }) => ` ${(percent * 100).toFixed(0)}`}
            >
              {categoryData.map((entry) => (
                <Cell
                  key={entry.name}
                  fill={categoryColorMap[entry.name] || '#CCCCCC'}
                />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Expenses by Status
          </Typography>
          <BarChart
            width={320}
            height={320}
            data={statusData}
            margin={{ left: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="value" fill="#8884d8" />
          </BarChart>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />
    </Box>
  );
}